import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Typography,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Fade,
  Menu,
  ListItem,
  ListItemButton,
  Checkbox,
  FormControlLabel,
  Badge,
  Container,
  Avatar,
} from '@mui/material';
import {
  Search,
  Recycling,
  FilterList,
  Inventory,
  Receipt,
  ShoppingCart,
  Favorite,
  LocalDrink,
  FiberSmartRecord,
  DeleteOutline,
  ChevronLeft,
  ChevronRight,
  ArrowForward,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getProducts, getStorefrontCategories } from '../utils/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { Product, StorefrontCategory } from '../types';
import { colors } from '../utils/constants';
import FavoriteButton from '../components/FavoriteButton';
import AddToCartButton from '../components/AddToCartButton';

const HomeProductsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const navigate = useNavigate();
  const { cart } = useCart();
  const { user } = useAuth();
  const { favorites } = useFavorites();

  const cartItemCount = cart?.items?.reduce((total: number, item: any) => total + (item.numberOfCases || 0), 0) || 0;

  const { data: storefrontCategories = [] } = useQuery({
    queryKey: ['storefront-categories'],
    queryFn: () => getStorefrontCategories(),
  });

  // Reusable Types filter options
  const reusableTypes = React.useMemo(() => {
    return storefrontCategories.reduce<Record<string, string[]>>((acc, category: StorefrontCategory) => {
      acc[category.name] = category.key_words || [];
      return acc;
    }, {});
  }, [storefrontCategories]);

  const carouselCategories = React.useMemo(() => {
    return storefrontCategories
      .filter((category: StorefrontCategory) => Array.isArray(category.key_words) && category.key_words.length > 0)
      .map((category: StorefrontCategory) => {
        const categoryName = category.name;
        const key = categoryName.toLowerCase();

        if (key.includes('cup')) {
          return {
            categoryName,
            categoryIcon: <LocalDrink sx={{ fontSize: '2rem' }} />,
            filterValue: category.key_words.join(','),
            iconColor: '#E91E63',
          };
        }

        if (key.includes('return')) {
          return {
            categoryName,
            categoryIcon: <Inventory sx={{ fontSize: '2rem' }} />,
            filterValue: category.key_words.join(','),
            iconColor: '#9C27B0',
          };
        }

        if (key.includes('cutlery')) {
          return {
            categoryName,
            categoryIcon: <FiberSmartRecord sx={{ fontSize: '2rem' }} />,
            filterValue: category.key_words.join(','),
            iconColor: '#3F51B5',
          };
        }

        if (key.includes('container')) {
          return {
            categoryName,
            categoryIcon: <DeleteOutline sx={{ fontSize: '2rem' }} />,
            filterValue: category.key_words.join(','),
            iconColor: '#4CAF50',
          };
        }

        return {
          categoryName,
          categoryIcon: <Inventory sx={{ fontSize: '2rem' }} />,
          filterValue: category.key_words.join(','),
          iconColor: colors.primary,
        };
      });
  }, [storefrontCategories]);

  // Appearance filter options
  const appearanceOptions = ['frosted', 'clear', 'white'];

  // State for selected filters
  const [selectedReusableTypes, setSelectedReusableTypes] = useState<Set<string>>(new Set());
  const [selectedAppearance, setSelectedAppearance] = useState<Set<string>>(new Set());

  // Menu anchor states for dropdowns
  const [reusableTypesMenuAnchor, setReusableTypesMenuAnchor] = useState<null | HTMLElement>(null);
  const [appearanceMenuAnchor, setAppearanceMenuAnchor] = useState<null | HTMLElement>(null);

  // Initialize filters from URL query parameters ONLY on mount
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (!filterParam) {
      // No filter in URL, state already initialized as empty
      return;
    }

    const filterValues = filterParam.split(',').map(v => v.trim());

    const reusableTypeValues = new Set<string>();
    const appearanceValues = new Set<string>();

    filterValues.forEach(value => {
      // Check if it's an appearance option
      if (appearanceOptions.includes(value)) {
        appearanceValues.add(value);
      } else {
        // Otherwise it's a reusable type
        reusableTypeValues.add(value);
      }
    });

    setSelectedReusableTypes(reusableTypeValues);
    setSelectedAppearance(appearanceValues);
    // Only run on mount - we don't want to re-sync from URL after that
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create stable query key to prevent unnecessary re-fetches
  const queryKey = React.useMemo(() => {
    const reusableTypesKey = Array.from(selectedReusableTypes).sort().join(',');
    const appearanceKey = Array.from(selectedAppearance).sort().join(',');
    return ['products', appliedSearchTerm, reusableTypesKey, appearanceKey];
  }, [appliedSearchTerm, selectedReusableTypes, selectedAppearance]);

  const { data: products = [], isLoading, error } = useQuery({
    queryKey,
    queryFn: () => {
      const searchTerms = [];

      // Add reusable type filters
      if (selectedReusableTypes.size > 0) {
        searchTerms.push(...Array.from(selectedReusableTypes));
      }

      // Add appearance filters
      if (selectedAppearance.size > 0) {
        searchTerms.push(...Array.from(selectedAppearance));
      }

      // Add search term
      if (appliedSearchTerm) {
        searchTerms.push(appliedSearchTerm);
      }

      const combinedSearch = searchTerms.length > 0 ? searchTerms.join(',') : undefined;
      return getProducts(combinedSearch);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
  });

  const updateURLFilters = React.useCallback((reusableTypes: Set<string>, appearance: Set<string>) => {
    // Update URL params WITHOUT triggering navigation/reload
    // Use a functional update to avoid depending on searchParams
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      const allFilters = Array.from(reusableTypes).concat(Array.from(appearance));

      if (allFilters.length > 0) {
        newParams.set('filter', allFilters.join(','));
      } else {
        newParams.delete('filter');
      }

      // Only update if different
      const currentFilter = prevParams.get('filter') || '';
      const newFilter = allFilters.join(',');

      if (currentFilter === newFilter) {
        return prevParams; // Return same object to prevent update
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Handler functions for filter checkboxes
  const handleReusableTypeChange = React.useCallback((category: string, checked: boolean) => {
    const newSet = new Set(selectedReusableTypes);
    const categoryValues = reusableTypes[category as keyof typeof reusableTypes] || [];

    if (checked) {
      // Add all values for this category
      categoryValues.forEach(v => newSet.add(v));
    } else {
      // Remove all values for this category
      categoryValues.forEach(v => newSet.delete(v));
    }

    setSelectedReusableTypes(newSet);
    updateURLFilters(newSet, selectedAppearance);
  }, [selectedReusableTypes, selectedAppearance, reusableTypes, updateURLFilters]);

  const handleAppearanceChange = React.useCallback((value: string, checked: boolean) => {
    const newSet = new Set(selectedAppearance);
    if (checked) {
      newSet.add(value);
    } else {
      newSet.delete(value);
    }
    setSelectedAppearance(newSet);
    updateURLFilters(selectedReusableTypes, newSet);
  }, [selectedReusableTypes, selectedAppearance, updateURLFilters]);

  // Menu handlers
  const handleReusableTypesMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setReusableTypesMenuAnchor(event.currentTarget);
  };

  const handleReusableTypesMenuClose = () => {
    setReusableTypesMenuAnchor(null);
  };

  const handleAppearanceMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAppearanceMenuAnchor(event.currentTarget);
  };

  const handleAppearanceMenuClose = () => {
    setAppearanceMenuAnchor(null);
  };

  // Close menus on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (reusableTypesMenuAnchor || appearanceMenuAnchor) {
        handleReusableTypesMenuClose();
        handleAppearanceMenuClose();
      }
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [reusableTypesMenuAnchor, appearanceMenuAnchor]);

  // Check if a category is fully selected
  const isCategorySelected = React.useCallback((category: string): boolean => {
    const categoryValues = reusableTypes[category as keyof typeof reusableTypes] || [];
    return categoryValues.every(value => selectedReusableTypes.has(value));
  }, [selectedReusableTypes, reusableTypes]);

  // Get count of selected reusable types (count categories, not individual terms)
  const getSelectedReusableTypesCount = React.useMemo(() => {
    return Object.keys(reusableTypes).filter(category =>
      isCategorySelected(category)
    ).length;
  }, [isCategorySelected, reusableTypes]);

  const getSelectedAppearanceCount = React.useMemo(() => {
    return selectedAppearance.size;
  }, [selectedAppearance]);

  const filteredProducts = (products as Product[])
    // Filter out products that should not be shown (default to visible if flag missing)
    .filter((product) => {
      const visible = (product.show_on_storefront ?? true) && (product.is_active ?? true) && !(product.is_demo === true);
      return visible;
    });

  // Track if this is the initial load (no products yet)
  const isInitialLoad = !products || products.length === 0;

  // Hide category carousels when there's a search term
  const showCategoryCarousels = !appliedSearchTerm && selectedReusableTypes.size === 0 && selectedAppearance.size === 0;

  // ProductCarousel component inline
  const ProductCarousel = ({
    categoryName,
    categoryIcon,
    filterValue,
    iconColor = colors.primary
  }: {
    categoryName: string;
    categoryIcon: React.ReactNode;
    filterValue: string;
    iconColor?: string;
  }) => {
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    const { data: carouselProducts = [] } = useQuery({
      queryKey: ['products', 'carousel', filterValue],
      queryFn: () => getProducts(filterValue),
    });

    const carouselFilteredProducts = (carouselProducts as Product[])
      .filter((product) => {
        const visible = (product.show_on_storefront ?? true) &&
                       (product.is_active ?? true) &&
                       !(product.is_demo === true);
        return visible;
      });

    if (carouselFilteredProducts.length === 0) {
      return null; // Don't show empty carousels
    }

    const scroll = (direction: 'left' | 'right') => {
      if (scrollContainerRef.current) {
        const scrollAmount = 300;
        const newPosition = scrollContainerRef.current.scrollLeft +
          (direction === 'right' ? scrollAmount : -scrollAmount);
        scrollContainerRef.current.scrollTo({
          left: newPosition,
          behavior: 'smooth',
        });
      }
    };

    const handleViewAll = () => {
      // Set filters instead of navigating
      const filterValues = filterValue.split(',').map(v => v.trim());
      const reusableTypeValues = new Set<string>();
      const appearanceValues = new Set<string>();

      filterValues.forEach(value => {
        if (appearanceOptions.includes(value)) {
          appearanceValues.add(value);
        } else {
          reusableTypeValues.add(value);
        }
      });

      setSelectedReusableTypes(reusableTypeValues);
      setSelectedAppearance(appearanceValues);
      updateURLFilters(reusableTypeValues, appearanceValues);
    };

    return (
      <Box sx={{ py: 3, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        {/* Section Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: { xs: 1, sm: 0 },
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Box
              sx={{
                color: iconColor,
                mr: { xs: 1, sm: 1.5 },
                display: 'flex',
                fontSize: { xs: '1.5rem', sm: '2rem' },
              }}
            >
              {categoryIcon}
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="600" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                {categoryName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {carouselFilteredProducts.length} products available
              </Typography>
            </Box>
          </Box>

          <Button
            variant="text"
            endIcon={<ArrowForward />}
            onClick={handleViewAll}
            sx={{
              color: iconColor,
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              '&:hover': {
                backgroundColor: `${iconColor}10`,
              },
            }}
          >
            View All
          </Button>
        </Box>

        {/* Carousel Container */}
        <Box sx={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
          {/* Left Arrow */}
          <IconButton
            sx={{
              position: 'absolute',
              left: { xs: 4, md: 8 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'white',
              boxShadow: 2,
              '&:hover': {
                bgcolor: 'white',
                boxShadow: 4,
              },
              display: { xs: 'none', md: 'flex' },
            }}
            onClick={() => scroll('left')}
          >
            <ChevronLeft />
          </IconButton>

          {/* Products Scroll Container */}
          <Box
            ref={scrollContainerRef}
            sx={{
              display: 'flex',
              overflowX: 'auto',
              overflowY: 'hidden',
              gap: 2,
              pb: 2,
              width: '100%',
              maxWidth: '100%',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: colors.primary,
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: colors.accent,
                },
              },
            }}
          >
            {carouselFilteredProducts.map((product: Product, index: number) => (
              <Fade in timeout={300 + index * 50} key={product.id}>
                <Card
                  sx={{
                    minWidth: { xs: 200, sm: 240, md: 260 },
                    maxWidth: { xs: 200, sm: 240, md: 260 },
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)',
                    },
                  }}
                  onClick={() => navigate(`/products/${product.id}`)}
                >
                  {/* Product Image */}
                  <Box
                    sx={{
                      height: 180,
                      p: 2,
                      bgcolor: 'grey.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    {/* Favorite Button */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1,
                      }}
                    >
                      <FavoriteButton product={product} size="small" />
                    </Box>
                    <CardMedia
                      component="img"
                      image={product.image_url || '/placeholder-product.png'}
                      alt={product.name}
                      sx={{
                        objectFit: 'contain',
                        height: '100%',
                        width: 'auto',
                        maxWidth: '100%',
                      }}
                      onError={(e) => {
                        e.currentTarget.src = '/placeholder-product.png';
                      }}
                    />
                  </Box>

                  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ p: 2, pb: 1 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="600"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          minHeight: '2.5em',
                        }}
                      >
                        {product.name}
                      </Typography>
                    </CardContent>

                    <CardActions sx={{ mt: 'auto', p: 0 }}>
                      <AddToCartButton product={product} size="small" />
                    </CardActions>
                  </Box>
                </Card>
              </Fade>
            ))}
          </Box>

          {/* Right Arrow */}
          <IconButton
            sx={{
              position: 'absolute',
              right: { xs: 4, md: 8 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'white',
              boxShadow: 2,
              '&:hover': {
                bgcolor: 'white',
                boxShadow: 4,
              },
              display: { xs: 'none', md: 'flex' },
            }}
            onClick={() => scroll('right')}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ width: '100%', maxWidth: '100%', px: { xs: 2, sm: 3 }, boxSizing: 'border-box' }}>
      <Box sx={{ py: 3, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
        {/* Compact Header Section */}
        <Card
          elevation={0}
          sx={{
            mb: 4,
            p: { xs: 2, md: 3 },
            background: `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.accent}08 100%)`,
            border: `1px solid ${colors.primary}20`,
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            {/* Left: Avatar and Greeting */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
              <Avatar
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  bgcolor: colors.primary,
                  fontSize: { xs: '1rem', sm: '1.2rem' },
                  fontWeight: 600,
                }}
              >
                {user?.first_name?.[0] || user?.email?.[0]?.toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Welcome back, {user?.first_name || 'User'}!
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Discover sustainable packaging solutions
                </Typography>
              </Box>
            </Box>

            {/* Right: Quick Stats */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ ml: { xs: 0, md: 'auto' } }}
            >
              <Chip
                icon={<ShoppingCart />}
                label={`${cartItemCount} in Cart`}
                onClick={() => navigate('/cart')}
                clickable
                sx={{
                  bgcolor: 'white',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: `${colors.primary}10`,
                  },
                }}
              />
              <Chip
                icon={<Favorite />}
                label={`${favorites.length} Favorites`}
                onClick={() => navigate('/favorites')}
                clickable
                sx={{
                  bgcolor: 'white',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: `${colors.primary}10`,
                  },
                }}
              />
              <Chip
                icon={<Receipt />}
                label="My Orders"
                onClick={() => navigate('/orders')}
                clickable
                sx={{
                  bgcolor: 'white',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: `${colors.primary}10`,
                  },
                }}
              />
            </Stack>
          </Box>
        </Card>

        {/* Search and Filters */}
        <Card
          elevation={0}
          variant="outlined"
          sx={{
            p: { xs: 2, md: 3 },
            mb: 4,
            borderColor: 'grey.300',
            borderRadius: 3
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by product name or label"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setAppliedSearchTerm(searchTerm);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {searchTerm !== appliedSearchTerm && (
                      <Button
                        onClick={() => setAppliedSearchTerm(searchTerm)}
                        variant="contained"
                        size="small"
                        sx={{ ml: 2, borderRadius: 2 }}
                      >
                        Search
                      </Button>
                    )}
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 }
              }}
              sx={{ flex: 1, minWidth: { xs: '100%', md: 200 } }}
            />

            {/* Filter Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexShrink: 0,
                width: { xs: '100%', md: 'auto' },
                overflowX: { xs: 'auto', md: 'visible' },
                overflowY: 'hidden',
                pr: { xs: 1, md: 0 },
                pb: { xs: 0.5, md: 0 },
                '& > :last-child': {
                  mr: { xs: 0.5, md: 0 },
                },
              }}
            >
              <Badge badgeContent={getSelectedReusableTypesCount} color="primary" invisible={getSelectedReusableTypesCount === 0}>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={handleReusableTypesMenuOpen}
                  sx={{
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.50',
                    }
                  }}
                >
                  Reusable Types
                </Button>
              </Badge>

              <Badge badgeContent={getSelectedAppearanceCount} color="primary" invisible={getSelectedAppearanceCount === 0}>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={handleAppearanceMenuOpen}
                  sx={{
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.50',
                    }
                  }}
                >
                  Appearance
                </Button>
              </Badge>
            </Box>
          </Box>
        </Card>

        {/* Reusable Types Menu */}
        <Menu
          anchorEl={reusableTypesMenuAnchor}
          open={Boolean(reusableTypesMenuAnchor)}
          onClose={handleReusableTypesMenuClose}
          keepMounted={false}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          disableAutoFocusItem
          disableRestoreFocus
          MenuListProps={{
            'aria-labelledby': 'reusable-types-filter',
          }}
          PaperProps={{
            sx: {
              maxHeight: 400,
              width: 320,
              mt: 1,
            }
          }}
        >
          <Box sx={{ p: 1 }}>
            <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 600 }}>
              Reusable Types
            </Typography>
            <Divider />
            <Stack spacing={0} sx={{ mt: 1 }}>
              {Object.entries(reusableTypes).map(([category, values]) => (
                <ListItem
                  key={category}
                  disablePadding
                  sx={{ py: 0.5 }}
                >
                  <ListItemButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReusableTypeChange(category, !isCategorySelected(category));
                    }}
                    sx={{ py: 0.5, px: 2 }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isCategorySelected(category)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleReusableTypeChange(category, e.target.checked);
                          }}
                          size="small"
                          sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="500">
                            {category}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {values.join(', ')}
                          </Typography>
                        </Box>
                      }
                      sx={{ m: 0, width: '100%' }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </Stack>
          </Box>
        </Menu>

        {/* Appearance Menu */}
        <Menu
          anchorEl={appearanceMenuAnchor}
          open={Boolean(appearanceMenuAnchor)}
          onClose={handleAppearanceMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          disableAutoFocusItem
          disableRestoreFocus
          MenuListProps={{
            'aria-labelledby': 'appearance-filter',
          }}
          PaperProps={{
            sx: {
              maxHeight: 400,
              width: 200,
              mt: 1,
            }
          }}
        >
          <Box sx={{ p: 1 }}>
            <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 600 }}>
              Appearance
            </Typography>
            <Divider />
            <Stack spacing={0} sx={{ mt: 1 }}>
              {appearanceOptions.map((option) => (
                <ListItem
                  key={option}
                  disablePadding
                  sx={{ py: 0.5 }}
                >
                  <ListItemButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAppearanceChange(option, !selectedAppearance.has(option));
                    }}
                    sx={{ py: 0.5, px: 2 }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedAppearance.has(option)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleAppearanceChange(option, e.target.checked);
                          }}
                          size="small"
                          sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {option}
                        </Typography>
                      }
                      sx={{ m: 0, width: '100%' }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </Stack>
          </Box>
        </Menu>

        {/* Category Carousels - Only show when no search/filters applied */}
        {showCategoryCarousels && (
          <>
            {carouselCategories.map((category) => (
              <React.Fragment key={category.categoryName}>
                <Box sx={{ mb: 3, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
                  <ProductCarousel
                    categoryName={category.categoryName}
                    categoryIcon={category.categoryIcon}
                    filterValue={category.filterValue}
                    iconColor={category.iconColor}
                  />
                </Box>
                <Divider sx={{ my: 4 }} />
              </React.Fragment>
            ))}
          </>
        )}

        {/* Error State - only show if there's an error */}
        {error && (
          <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
            Failed to load products. Please try again later.
          </Alert>
        )}

        {/* Results Count */}
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {isLoading && !isInitialLoad ? (
            'Loading products...'
          ) : (
            `Showing ${filteredProducts.length} products`
          )}
        </Typography>

        {/* Loading State - only show full screen on initial load */}
        {isLoading && isInitialLoad ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {/* Products Grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                },
                gap: { xs: 2, sm: 3 },
                position: 'relative',
                opacity: isLoading && !isInitialLoad ? 0.6 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              {isLoading && !isInitialLoad && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
              )}
              {filteredProducts.map((product: Product, index: number) => (
                <Fade in timeout={300 + index * 100} key={product.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      '&:hover': {
                        boxShadow: (theme) => theme.shadows[8],
                      },
                    }}
                    onClick={() => navigate(`/products/${product.id}`)}
                  >
                    {/* Product Image Container */}
                    <Box
                      sx={{
                        height: 200,
                        p: 2,
                        bgcolor: 'grey.50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      {/* Favorite Button */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                        }}
                      >
                        <FavoriteButton product={product} size="small" />
                      </Box>
                      <CardMedia
                        component="img"
                        image={product.image_url || '/placeholder-product.png'}
                        alt={product.name}
                        sx={{
                          objectFit: 'contain',
                          height: '100%',
                          width: 'auto',
                          maxWidth: '100%',
                        }}
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder-product.png';
                        }}
                      />
                    </Box>

                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ p: 0 }}>
                        {/* Product Name */}
                        <Typography variant="h6" fontWeight="bold" sx={{ px: 2, pt: 2 }}>
                          {product.name}
                        </Typography>
                      </CardContent>

                      <CardActions sx={{ mt: 'auto', p: 0 }}>
                        <AddToCartButton product={product} />
                      </CardActions>
                    </Box>
                  </Card>
                </Fade>
              ))}
            </Box>
          </>
        )}

        {/* Empty State */}
        {!isLoading && filteredProducts.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              px: 2,
            }}
          >
            <Recycling sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" sx={{ mb: 1 }}>
              No products found
            </Typography>
            <Typography variant="body1" color="text.disabled">
              Try adjusting your search or filter criteria
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default HomeProductsPage;
